import { ChangeEvent, MouseEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Check, ChevronDown, X } from 'lucide-react';

import { OutsideAlerter } from '../utilities/OutsideAlerter';

export interface ISelectOption {
    value: string | null;
    label: string;
};

interface ISelect {
    selected: ISelectOption | null;
    preBtnIcon?: ReactNode;
    postBtnIcon?: ReactNode;
    options: ISelectOption[];
    size?: 'sm' | 'md' | 'lg';
    fluid?: boolean;
    disabled?: boolean;
    onSelect: (value: ISelectOption) => void;
};
export const Select = (props: ISelect) => {
    const { selected, preBtnIcon, postBtnIcon, options, size = 'sm', fluid, disabled, onSelect } = props;

    const mainWidthItemRef = useRef<HTMLButtonElement>(null);
    const hiddenMeasureRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState<boolean>(false);

    useLayoutEffect(() => {
        if (!hiddenMeasureRef.current || !mainWidthItemRef.current) return;
        if (fluid) return;

        let maxWidth = 0;

        const buttons = hiddenMeasureRef.current.children;

        for (const btn of Array.from(buttons)) {
            const width = (btn as HTMLElement).getBoundingClientRect().width;
            maxWidth = Math.max(maxWidth, width);
        };

        mainWidthItemRef.current.style.minWidth = `${maxWidth}px`;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, fluid]);

    const handleToggle = () => {
        if (disabled) return;

        setOpen(!open);
    };

    const handleSelect = (option: ISelectOption) => {
        if (disabled) return;

        onSelect(option);
        setOpen(false);
    };

    return (
        <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
            <div className={`select ${fluid ? 'select--fluid' : ''} select--${size} ${open ? 'select--open' : ''}`}>
                <button
                    ref={mainWidthItemRef}
                    className={`select--main-btn ${disabled ? 'select--main-btn--disabled' : ''}`}
                    onClick={handleToggle}
                >
                    {preBtnIcon}
                    <div className='select--main-btn--right'>
                        <span>{selected?.label ?? '-- Select --'}</span>
                        {postBtnIcon ?? <ChevronDown style={{ rotate: open ? "180deg" : "0deg" }} />}
                    </div>
                </button>

                <div ref={hiddenMeasureRef} className="select--hidden-measure">
                    {options.map(option => (
                        <button key={option.value} className='select--main-btn'>
                            {preBtnIcon}
                            <div className='select--main-btn--right'>
                                <span>{option.label}</span>
                                {postBtnIcon ?? <ChevronDown />}
                            </div>
                        </button>
                    ))}
                </div>

                {open &&
                    <div className='select--options'>
                        {options.map(option => (
                            <button
                                key={option.value}
                                onClick={() => handleSelect(option)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                }
            </div>
        </OutsideAlerter>
    );
};

export interface ICustomSelect1BaseProps {
    placeholder?: string;
    preInputIcon?: ReactNode;
    postInputIcon?: ReactNode;
    size?: 'sm' | 'md' | 'lg';
    fluid?: boolean;
    disabled?: boolean;
};
interface ICustomSelect1Props<TOption extends ISelectOption> extends ICustomSelect1BaseProps {
    options: TOption[];
    selected: TOption[];
    onSelectToggle: (value: TOption) => void;
    renderOption: (option: TOption, forHiddenMeasure?: boolean) => ReactNode;
    isOptionDisabled?: (option: TOption) => boolean;
    onUnselect?: (option: TOption) => void;
};
export const CustomSelect1 = <TOption extends ISelectOption>(props: ICustomSelect1Props<TOption>) => {
    const {
        placeholder,
        preInputIcon,
        postInputIcon,
        options,
        selected,
        size = 'sm',
        fluid,
        disabled,
        onSelectToggle,
        renderOption,
        isOptionDisabled,
        onUnselect,
    } = props;

    const mainWidthItemRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const hiddenMeasureRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [filteredOptions, setFilteredOptions] = useState<TOption[]>(options);

    useLayoutEffect(() => {
        if (!hiddenMeasureRef.current || !mainWidthItemRef.current) return;
        if (fluid) return;

        let maxWidth = 0;

        const buttons = hiddenMeasureRef.current.querySelectorAll('div > button');

        for (const btn of Array.from(buttons)) {
            const width = (btn as HTMLElement).getBoundingClientRect().width;
            maxWidth = Math.max(maxWidth, width);
        };

        mainWidthItemRef.current.style.minWidth = `${maxWidth}px`;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, fluid]);

    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    const handleOpen = () => {
        if (disabled) return;

        setOpen(true);
        inputRef.current?.focus();
    };

    const handleToggle = (e: MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;

        setOpen(prev => !prev);
        if (!open) {
            inputRef.current?.focus();
        }
    };

    const handleSelectToggle = (option: TOption) => {
        if (disabled) return;

        onSelectToggle(option);
    };

    const handleDeselect = (e: MouseEvent, option: TOption) => {
        e.stopPropagation();
        onUnselect?.(option);
        handleSelectToggle(option);
    };

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        setFilteredOptions(options.filter(option => option.label.toLowerCase().includes(value.toLowerCase())));
    };

    const clearSearch = () => {
        setSearchValue('');
        setFilteredOptions(options);
    };

    return (
        <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
            <div className={`custom-select1 ${fluid ? 'custom-select1--fluid' : ''} custom-select1--${size} ${open ? 'custom-select1--open' : ''}`}>
                <div
                    ref={mainWidthItemRef}
                    className={`custom-select1--input-container ${disabled ? 'custom-select1--input-container--disabled' : ''}`}
                    onClick={handleOpen}
                >
                    {preInputIcon}
                    <div className='custom-select1--input-container--right'>
                        <div className='custom-select1--values'>
                            {selected.map(option => (
                                <span key={`${option.value ?? 'null'}-${option.label}`} className='custom-select1--chip'>
                                    <span>{option.label}</span>
                                    <button
                                        type="button"
                                        onClick={e => handleDeselect(e, option)}
                                        disabled={disabled}
                                        aria-label={`Remove ${option.label}`}
                                    >
                                        <X />
                                    </button>
                                </span>
                            ))}
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchValue}
                                onChange={handleSearchChange}
                                placeholder={placeholder ?? 'Type to search'}
                                disabled={disabled}
                            />
                        </div>
                        <button
                            type="button"
                            className='custom-select1--toggle'
                            onClick={handleToggle}
                            disabled={disabled}
                            aria-label={open ? 'Close options' : 'Open options'}
                        >
                            {postInputIcon ?? <ChevronDown style={{ rotate: open ? '180deg' : '0deg' }} />}
                        </button>
                    </div>
                </div>

                <div ref={hiddenMeasureRef} className="custom-select1--hidden-measure">
                    <div className='custom-select1--options'>
                        <button>No results found</button>
                        {options.map(option => (
                            <button key={option.value}>
                                {renderOption(option, true)}
                            </button>
                        ))}
                    </div>
                </div>

                {open && (
                    <div className='custom-select1--options'>
                        {!filteredOptions.length && <button>No results found</button>}
                        {!!filteredOptions.length && filteredOptions.map(option => (
                            <button
                                key={option.value}
                                onClick={() => {
                                    handleSelectToggle(option);
                                    clearSearch();
                                }}
                                disabled={isOptionDisabled?.(option) ?? disabled}
                            >
                                {renderOption(option)}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </OutsideAlerter>
    );
};

export interface ICustomSelect2Option extends ISelectOption {
    section?: 'primary' | 'default';
    checked?: boolean;
};
interface ICustomSelect2Props<TOption extends ICustomSelect2Option> {
    selectedLabel: string;
    options: TOption[];
    size?: 'sm' | 'md' | 'lg';
    fluid?: boolean;
    disabled?: boolean;
    onSelect: (value: TOption) => void;
};
export const CustomSelect2 = <TOption extends ICustomSelect2Option>(props: ICustomSelect2Props<TOption>) => {
    const {
        selectedLabel,
        options,
        size = 'sm',
        fluid,
        disabled,
        onSelect,
    } = props;

    const mainWidthItemRef = useRef<HTMLButtonElement>(null);
    const hiddenBtnsRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState<boolean>(false);

    useLayoutEffect(() => {
        if (!hiddenBtnsRef.current || !mainWidthItemRef.current) return;
        if (fluid) return;

        let maxWidth = 0;

        const buttons = hiddenBtnsRef.current.querySelectorAll('.custom-select2--option-container > button');

        console.log(buttons);


        for (const btn of Array.from(buttons)) {
            const width = (btn as HTMLElement).getBoundingClientRect().width;
            maxWidth = Math.max(maxWidth, width);
        };

        mainWidthItemRef.current.style.minWidth = `${maxWidth}px`;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, selectedLabel, fluid]);

    const handleToggle = () => {
        if (disabled) return;

        setOpen(!open);
    };

    const handleSelect = (option: TOption) => {
        if (disabled) return;

        onSelect(option);
    };

    const Options = (props: { forHiddenMeasure?: boolean }) => {
        const { forHiddenMeasure = false } = props;

        return (
            <div className='custom-select2--options'>
                {options.map((option, idx) => {
                    const nextOption = options[idx + 1];
                    const showDivider = option.section === 'primary' && nextOption?.section === 'default';

                    return (
                        <div className='custom-select2--option-container' key={`${option.section ?? 'default'}-${option.value}`}>
                            <button
                                type="button"
                                className={`custom-select2--option ${option.section === 'primary' ? 'custom-select2--option--primary' : ''}`}
                                onClick={() => forHiddenMeasure ? null : handleSelect(option)}
                            >
                                <span>{option.label}</span>
                                {(forHiddenMeasure || option.checked) && <Check />}
                            </button>
                            {(forHiddenMeasure || showDivider) && <div className="custom-select2--divider" />}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
            <div className={`custom-select2 ${fluid ? 'custom-select2--fluid' : ''} custom-select2--${size} ${open ? 'custom-select2--open' : ''}`}>
                <button
                    ref={mainWidthItemRef}
                    type="button"
                    className={`custom-select2--trigger ${disabled ? 'custom-select2--trigger--disabled' : ''}`}
                    onClick={handleToggle}
                    disabled={disabled}
                >
                    <div className='custom-select2--trigger--right'>
                        <span>{selectedLabel}</span>
                        <ChevronDown style={{ rotate: open ? '180deg' : '0deg' }} />
                    </div>
                </button>

                <div ref={hiddenBtnsRef} className="custom-select2--hidden-measure">
                    <Options forHiddenMeasure={true} />
                </div>

                {open && <Options />}
            </div>
        </OutsideAlerter>
    );
};